import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import CircleIcon from "@mui/icons-material/Circle";
import { customGetAPI } from "services/utils/CustomAPI";
import { useFetch } from "services/hooks/useFetch";
import {
  designColors,
  designRadii,
  designShell,
} from "services/constants/designTokens";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { Loader } from "components/Loader";
import {
  canonicalLeaveStatus,
  leaveStatusVisual,
} from "pages/TeamZone/Approve/Leave/LeaveStatusCards";
import { PermissionsDetails } from "./permissionDetails";
import { LeaveHistory } from "./LeaveHistory";
import { CancelLeaveDialog } from "./CancelLeave";
import { YearHeaderFilter } from "./YearHeaderFilter";

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) =>
  Number.isInteger(value) ? `${value}` : value.toFixed(1);

// API dates arrive as "DD-MM-YYYY" strings (same format CancelLeave.js parses
// with moment elsewhere in this module) — newest first regardless of
// whatever order the backend happens to send.
const sortByDateDesc = (items, key) =>
  [...(items || [])].sort(
    (a, b) =>
      moment(b?.[key], "DD-MM-YYYY").valueOf() -
      moment(a?.[key], "DD-MM-YYYY").valueOf(),
  );

const StatusChip = ({ status }) => {
  const visual = leaveStatusVisual(status);
  return (
    <Mui.Chip
      size="small"
      icon={
        <CircleIcon
          sx={{
            fontSize: "9px !important",
            color: `${visual.accent} !important`,
          }}
        />
      }
      label={canonicalLeaveStatus(status) || "N/A"}
      sx={{
        height: 24,
        borderRadius: designRadii.pill,
        backgroundColor: visual.tint,
        color: visual.accent,
        "& .MuiChip-label": { px: 1, fontSize: 12, fontWeight: 600 },
      }}
    />
  );
};

const LeaveTypeCard = ({ item, active, onClick }) => {
  const accent = item?.leave_color || designColors.tealDark;
  return (
    <Mui.Box
      onClick={onClick}
      sx={{
        flex: "1 1 130px",
        minWidth: 120,
        p: 1.25,
        borderRadius: designRadii.card,
        border: `1px solid ${active ? accent : designColors.slate200}`,
        backgroundColor: active ? `${accent}14` : designColors.white,
        cursor: "pointer",
      }}
    >
      <Mui.Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: designColors.slate600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {item?.name || "N/A"}
      </Mui.Typography>
      <Mui.Stack
        direction="row"
        alignItems="baseline"
        spacing={0.4}
        sx={{ mt: 0.4 }}
      >
        <Mui.Typography sx={{ fontSize: 19, fontWeight: 700, color: accent }}>
          {formatCount(safeNumber(item?.available_leave))}
        </Mui.Typography>
        <Mui.Typography sx={{ fontSize: 12, color: designColors.slate500 }}>
          of {formatCount(safeNumber(item?.credited_leave))}
        </Mui.Typography>
      </Mui.Stack>
    </Mui.Box>
  );
};

const StatBlock = ({ label, value, icon: Icon }) => (
  <Mui.Stack
    direction="row"
    spacing={1.1}
    alignItems="center"
    sx={{
      flex: "1 1 45%",
      minWidth: 150,
      p: 1.5,
      borderRadius: designRadii.card,
      border: `1px solid ${designColors.slate200}`,
      backgroundColor: designColors.white,
    }}
  >
    <Mui.Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 1.5,
        backgroundColor: designColors.slateBg,
        color: designColors.slate600,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
    </Mui.Box>
    <Mui.Box sx={{ minWidth: 0 }}>
      <Mui.Typography sx={{ fontSize: 11.5, color: designColors.slate500 }}>
        {label}
      </Mui.Typography>
      <Mui.Typography
        sx={{ fontSize: 15, fontWeight: 700, color: designColors.slate900 }}
      >
        {value ?? "N/A"}
      </Mui.Typography>
    </Mui.Box>
  </Mui.Stack>
);

const RECENT_HISTORY_LIMIT = 4;

const historyCellSx = {
  py: 1,
  fontSize: 13,
  color: designColors.slate800,
  borderBottom: `1px solid ${designColors.slate100}`,
  whiteSpace: "nowrap",
};

export const LeaveBalanceDetailPanel = ({
  open,
  onClose,
  selectedUser,
  expanded = false,
  onToggleExpand,
  checkAccess,
  onDataChanged,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, globalSearchValue } = useSelector(
    (state) => state.tokenReducer,
  );
  const { result: leaveYears } = useFetch(`${domain}leaves-years/`);

  const [tab, setTab] = useState("summary");
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [taxYearStartDateUser, setTaxYearStartDate] = useState("");
  const [taxYearEndDateUser, setTaxYearEndDate] = useState("");

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [leaveTypeDetail, setLeaveTypeDetail] = useState([]);
  const [singleLeaveTypeDetails, setSingleLeaveTypeDetails] = useState({});
  const [leaveTypeId, setLeaveTypeId] = useState(null);
  const [leaveTypeTakenLeaves, setLeaveTypeTakenLeaves] = useState([]);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  const [loadPermissionApi, setLoadPersmissionApi] = useState(false);

  const [masterData, setMasterData] = useState([]);
  const [leaveCreditDebitApi, setLeaveCreditDebitApi] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Reset to a fresh Summary tab each time a different employee's panel
  // opens, same as the old full-page PastLeaveDetails did on navigation.
  useEffect(() => {
    if (!open) return;
    setTab("summary");
    setShowAllHistory(false);
    setCancelTarget(null);
  }, [open, selectedUser?.user_id]);

  useEffect(() => {
    if (!leaveYears?.length || selectedYearId) return;
    const first = leaveYears[0];
    setSelectedYearId(first.id);
    setTaxYearStartDate(first.start_date?.split("-").reverse().join("-"));
    setTaxYearEndDate(first.end_date?.split("-").reverse().join("-"));
  }, [leaveYears, selectedYearId]);

  const handleYearChange = (year) => {
    if (!year) return;
    setSelectedYearId(year.id);
    setTaxYearStartDate(year.start_date?.split("-").reverse().join("-"));
    setTaxYearEndDate(year.end_date?.split("-").reverse().join("-"));
  };

  const yearFilterOptions = useMemo(
    () => (leaveYears || []).map((year) => ({ ...year, label: year.name })),
    [leaveYears],
  );

  useEffect(() => {
    if (
      !open ||
      !selectedUser?.user_id ||
      !taxYearStartDateUser ||
      !taxYearEndDateUser
    )
      return;
    const fetchSummary = async () => {
      setLoadingSummary(true);
      const res = await customGetAPI(
        `${domain}leave-summary-details/?user_id=${selectedUser.user_id}&start_date=${taxYearStartDateUser}&end_date=${taxYearEndDateUser}&company_id=${requestCompanyId}`,
      );
      if (res.ok) {
        const details = res?.responseDetails || [];
        setLeaveTypeDetail(details);
        setSingleLeaveTypeDetails(details[0] || {});
        setLeaveTypeId(details[0]?.leave_type_id ?? null);
        setLeaveTypeTakenLeaves(
          sortByDateDesc(details[0]?.leave_requests, "from_date"),
        );
      } else {
        setLeaveTypeDetail([]);
        setSingleLeaveTypeDetails({});
        setLeaveTypeTakenLeaves([]);
      }
      setLoadingSummary(false);
    };
    fetchSummary();
  }, [
    domain,
    open,
    selectedUser?.user_id,
    taxYearStartDateUser,
    taxYearEndDateUser, requestCompanyId
  ]);

  useEffect(() => {
    if (!open || tab !== "permissions") return;
    setLoadPersmissionApi(true);
  }, [
    open,
    tab,
    selectedUser?.user_id,
    taxYearStartDateUser,
    taxYearEndDateUser,
  ]);

  useEffect(() => {
    if (!open || tab !== "history") return;
    setLeaveCreditDebitApi(true);
  }, [
    open,
    tab,
    selectedUser?.user_id,
    taxYearStartDateUser,
    taxYearEndDateUser,
  ]);

  useEffect(() => {
    if (!leaveCreditDebitApi || !selectedUser?.user_id) return;
    const fetchLedger = async () => {
      setLoadingHistory(true);
      const res = await customGetAPI(
        `${domain}leave-summary-history/?user_id=${selectedUser.user_id}&start_date=${taxYearStartDateUser}&end_date=${taxYearEndDateUser}&company_id=${requestCompanyId}`,
      );
      setMasterData(res.ok ? sortByDateDesc(res?.responseDetails, "date") : []);
      setLoadingHistory(false);
      setLeaveCreditDebitApi(false);
    };
    fetchLedger();
  }, [
    domain,
    leaveCreditDebitApi,
    selectedUser?.user_id,
    taxYearEndDateUser,
    taxYearStartDateUser, requestCompanyId
  ]);

  const leaveCreditDebitDetails = useMemo(() => {
    const query = (globalSearchValue || "").toString().toLowerCase();
    if (!query) return masterData;
    return masterData.filter((item) =>
      ["date", "leave_type_name", "description", "createdby"].some((key) =>
        item[key]?.toString().toLowerCase().includes(query),
      ),
    );
  }, [globalSearchValue, masterData]);

  const handleSelectLeaveType = (item) => {
    setLeaveTypeId(item?.leave_type_id);
    setSingleLeaveTypeDetails(item || {});
    setLeaveTypeTakenLeaves(sortByDateDesc(item?.leave_requests, "from_date"));
    setShowAllHistory(false);
  };

  const handleCancelled = () => {
    setCancelTarget(null);
    onDataChanged?.();
    // Re-pull this employee's own summary so the panel reflects the
    // now-cancelled leave immediately, without waiting on the year/user
    // effect above (which only fires when those identifiers change).
    if (selectedUser?.user_id && taxYearStartDateUser && taxYearEndDateUser) {
      customGetAPI(
        `${domain}leave-summary-details/?user_id=${selectedUser.user_id}&start_date=${taxYearStartDateUser}&end_date=${taxYearEndDateUser}&company_id=${requestCompanyId}`,
      ).then((res) => {
        if (!res.ok) return;
        const details = res?.responseDetails || [];
        setLeaveTypeDetail(details);
        const current =
          details.find((item) => item.leave_type_id === leaveTypeId) ||
          details[0] ||
          {};
        setSingleLeaveTypeDetails(current);
        setLeaveTypeTakenLeaves(
          sortByDateDesc(current?.leave_requests, "from_date"),
        );
      });
    }
  };

  const employeeName = selectedUser?.user_name || "N/A";
  const visibleHistory = showAllHistory
    ? leaveTypeTakenLeaves
    : leaveTypeTakenLeaves.slice(0, RECENT_HISTORY_LIMIT);

  const summaryContent = loadingSummary ? (
    <Mui.Box sx={{ py: 6 }}>
      <Loader info="Loading..." />
    </Mui.Box>
  ) : (
    <Mui.Stack spacing={2.25}>
      <Mui.Box>
        <Mui.Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Mui.Typography
            sx={{ fontSize: 14, fontWeight: 700, color: designColors.slate900 }}
          >
            Leave Balances
          </Mui.Typography>
          <Mui.Typography sx={{ fontSize: 12, color: designColors.slate500 }}>
            (In Days)
          </Mui.Typography>
        </Mui.Stack>
        <Mui.Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {leaveTypeDetail.map((item) => (
            <LeaveTypeCard
              key={item.leave_type_id}
              item={item}
              active={item.leave_type_id === leaveTypeId}
              onClick={() => handleSelectLeaveType(item)}
            />
          ))}
        </Mui.Box>
      </Mui.Box>

      <Mui.Box
        sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, rowGap: 1.5 }}
      >
        <StatBlock
          label="Total Available"
          value={`${formatCount(safeNumber(singleLeaveTypeDetails?.available_leave))} days`}
          icon={AccountBalanceWalletRoundedIcon}
        />
        <StatBlock
          label="Expiring On"
          value={singleLeaveTypeDetails?.expiry_date || "N/A"}
          icon={EventBusyRoundedIcon}
        />
        <StatBlock
          label="Leave Taken This Year"
          value={`${formatCount(safeNumber(singleLeaveTypeDetails?.used_leave))} days`}
          icon={HourglassBottomRoundedIcon}
        />
        <StatBlock
          label="Upcoming Leave"
          value={`${formatCount(safeNumber(singleLeaveTypeDetails?.planned_leave))} days`}
          icon={TrendingUpRoundedIcon}
        />
      </Mui.Box>

      <Mui.Box sx={{ ...designShell.sectionCard, p: 1.75 }}>
        <Mui.Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Mui.Typography
            sx={{ fontSize: 14, fontWeight: 700, color: designColors.slate900 }}
          >
            Recent Leave History
          </Mui.Typography>
          {leaveTypeTakenLeaves.length > RECENT_HISTORY_LIMIT ? (
            <Mui.Button
              onClick={() => setShowAllHistory((prev) => !prev)}
              sx={{
                minWidth: 0,
                p: 0,
                textTransform: "none",
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              {showAllHistory ? "Show less" : "View all"}
            </Mui.Button>
          ) : null}
        </Mui.Stack>
        {visibleHistory.length ? (
          <Mui.TableContainer sx={{ overflowX: "auto" }}>
            <Mui.Table size="small" sx={{ minWidth: 480 }}>
              <Mui.TableHead>
                <Mui.TableRow>
                  {["Leave Type", "Dates", "Days", "Status", ...(checkAccess ? [""] : [])].map(
                    (label, index) => (
                      <Mui.TableCell
                        key={label || `action-${index}`}
                        sx={{
                          ...designShell.tableHeadCellBase,
                          py: 1,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </Mui.TableCell>
                    ),
                  )}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {visibleHistory.map((leave) => (
                  <Mui.TableRow key={leave.id} sx={designShell.tableRowHover}>
                    <Mui.TableCell sx={historyCellSx}>
                      {leave.name}
                    </Mui.TableCell>
                    <Mui.TableCell sx={historyCellSx}>
                      {leave.from_date}
                      {leave.from_date !== leave.to_date
                        ? ` – ${leave.to_date}`
                        : ""}
                    </Mui.TableCell>
                    <Mui.TableCell sx={historyCellSx}>
                      {leave.business_days} day
                      {safeNumber(leave.business_days) === 1 ? "" : "s"}
                    </Mui.TableCell>
                    <Mui.TableCell sx={historyCellSx}>
                      <StatusChip status={leave.status} />
                    </Mui.TableCell>
                    {checkAccess && (
                      <Mui.TableCell sx={{ ...historyCellSx, textAlign: "right" }}>
                        {leave.is_cancel_enabled &&
                        canonicalLeaveStatus(leave.status) === "Approved" ? (
                          <Mui.Button
                            size="small"
                            onClick={() => setCancelTarget(leave)}
                            sx={{
                              minWidth: 0,
                              px: 0.75,
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#DC2626",
                              textTransform: "none",
                            }}
                          >
                            Cancel
                          </Mui.Button>
                        ) : null}
                      </Mui.TableCell>
                    )}
                  </Mui.TableRow>
                ))}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
        ) : (
          <Mui.Typography
            sx={{ fontSize: 13, color: designColors.slate500, py: 1 }}
          >
            No leave requests found for this leave type.
          </Mui.Typography>
        )}
      </Mui.Box>
    </Mui.Stack>
  );

  if (!selectedUser?.user_id) return null;

  // Same shared chrome as the Approve Center panels (e.g. ShiftCorrection,
  // Permission) — GlobalSidePanel owns the "Open full view" icon
  // (OpenInNewOutlinedIcon -> "Collapse"/"Close" labeled buttons once
  // expanded) and the expand/portal mechanics, so this panel no longer
  // hand-rolls its own Drawer + full-page portal + icon toggle.
  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={onClose}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="Leave Balance"
        subtitle="Leave summary, permissions and history"
        headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        PaperProps={{
          sx: {
            backgroundColor: designColors.white,
            maxWidth: "92vw",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
          },
        }}
        breadcrumb={[
          "Leave Balances",
          { label: "Employees", onClick: onClose },
          employeeName,
        ]}
      >
        <Mui.Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <RequestDetailHeader
            userDetails={{
              image_url: selectedUser?.image_url,
              employee_id: selectedUser?.employee_id || "N/A",
              user_name: employeeName,
              department_name: selectedUser?.department || "N/A",
              designation: selectedUser?.designation,
            }}
          />

          <Mui.Box>
            <Mui.Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              variant="fullWidth"
              sx={designShell.panelTabs}
            >
              <Mui.Tab label="Summary" value="summary" />
              <Mui.Tab label="Permissions" value="permissions" />
              <Mui.Tab label="History" value="history" />
            </Mui.Tabs>
            {yearFilterOptions.length ? (
              <Mui.Box sx={{ width: "100%", mt: 1.5 }}>
                <YearHeaderFilter
                  options={yearFilterOptions}
                  selected={yearFilterOptions.find(
                    (item) => item.id === selectedYearId,
                  )}
                  onSelect={handleYearChange}
                />
              </Mui.Box>
            ) : null}
          </Mui.Box>

          {tab === "summary" && summaryContent}
          {tab === "permissions" && (
            <PermissionsDetails
              selectedUser={selectedUser}
              loadPermissionApi={loadPermissionApi}
              setLoadPersmissionApi={setLoadPersmissionApi}
              taxYearStartDateUser={taxYearStartDateUser}
              taxYearEndDateUser={taxYearEndDateUser}
            />
          )}
          {tab === "history" &&
            (loadingHistory ? (
              <Mui.Box sx={{ py: 6 }}>
                <Loader info="Loading..." />
              </Mui.Box>
            ) : (
              <LeaveHistory
                leaveCreditDebitDetails={leaveCreditDebitDetails}
                masterData={masterData}
              />
            ))}
        </Mui.Box>
      </GlobalSidePanel>
      <CancelLeaveDialog
        open={checkAccess && Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
        cancelLeaveData={cancelTarget}
        leaveYearId={selectedYearId}
        onCancelled={handleCancelled}
      />
    </>
  );
};
